import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import tickets from '../../models/tickets';
import helpers from '../../helpers/common';
import useRootHook from '../../hooks/useRoot';
import { Add } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { AclRouterLink } from '../ACL';
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';
import { Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';

const statusLabels = { open: 'Open', answered: 'Answered', closed: 'Closed' };
const statusColors = { open: 'warning', answered: 'success', closed: 'default' };

export default function TicketIndex() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createSubject, setCreateSubject] = useState('');
    const [createMessage, setCreateMessage] = useState('');
    const [createError, setCreateError] = useState('');
    const [refreshId, setRefreshId] = useState(0);
    const { setShowLoader, userData } = useRootHook();
    const isAdmin = userData && [helpers.roles.A, helpers.roles.S].includes(userData.role);

    useEffect(() => {
        setShowLoader(true);
        const filters = Object.fromEntries(searchParams.entries());
        tickets.index(filters)
            .then((res) => {
                if (res) setData(res);
                setShowLoader(false);
            })
            .catch(() => setShowLoader(false));
    }, [searchParams, refreshId]);

    const handleCreateSubmit = () => {
        setCreateError('');
        if (!createSubject.trim()) {
            setCreateError('Subject is required');
            return;
        }
        if (!createMessage.trim()) {
            setCreateError('Message is required');
            return;
        }
        setShowLoader(true);
        tickets.add({ subject: createSubject.trim(), message: createMessage.trim() })
            .then(() => {
                setCreateOpen(false);
                setCreateSubject('');
                setCreateMessage('');
                setShowLoader(false);
                setRefreshId((r) => r + 1);
            })
            .catch(() => setShowLoader(false));
    };

    if (!data) return null;

    return (
        <React.Fragment>
            <Grid size={12}>
                <PageHeader
                    title="Support tickets"
                    breadcrumbs={[['Dashboard', '/'], ['Tickets', '/tickets']]}
                    actions={
                        !isAdmin && (
                            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
                                New ticket
                            </Button>
                        )
                    }
                />
            </Grid>
            <Grid size={12}>
                <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto' }}>
                    <TableToolbar title="Tickets" />
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Status</TableCell>
                                {isAdmin && <TableCell>Author</TableCell>}
                                <TableCell>Created</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data.tickets || []).map((row) => (
                                <TableRow key={`ticket-${row.id}`}>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>
                                        <Link to={`/tickets/${row.id}`} component={RouterLink}>
                                            {helpers.cutLongString(row.subject, 60)}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={statusLabels[row.status] || row.status} color={statusColors[row.status] || 'default'} />
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell>
                                            {row.user_role === 'M' ? 'Merchant' : row.user_role === 'R' ? 'Referral' : 'Admin'} #{row.user_id}
                                        </TableCell>
                                    )}
                                    <TableCell>{helpers.getDateTime(row.created)}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" to={`/tickets/${row.id}`} component={RouterLink}>
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SectionCard>
            </Grid>
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New ticket</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Subject"
                        fullWidth
                        value={createSubject}
                        onChange={(e) => setCreateSubject(e.target.value)}
                        error={!!createError && !createMessage.trim()}
                    />
                    <TextField
                        margin="dense"
                        label="Message"
                        fullWidth
                        multiline
                        rows={4}
                        value={createMessage}
                        onChange={(e) => setCreateMessage(e.target.value)}
                        error={!!createError}
                        helperText={createError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateSubmit} variant="contained">Send</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
